<template>
  <div class="worker-shifts-page">
    <div class="header">
      <button @click="goBack" class="back-btn">← Back to Manager Dashboard</button>
      <div class="worker-info" v-if="worker">
        <h2>{{ worker.first_name }} {{ worker.last_name }}</h2>
        <span class="role-badge" :class="worker.role">{{ worker.role }}</span>
      </div>
    </div>

    <!-- Month Navigation -->
    <div class="month-navigation">
      <button @click="previousMonth" class="nav-btn">‹ Previous</button>
      <h3 class="current-month">{{ formatMonthYear(currentDate) }}</h3>
      <button @click="nextMonth" class="nav-btn">Next ›</button>
    </div>

    <!-- Summary Stats -->
    <div class="stats-summary" v-if="shifts.length > 0">
      <div class="stat-card">
        <span class="stat-label">Total Hours</span>
        <span class="stat-value">{{ totalHours }}h</span>
      </div>
      <div class="stat-card">
        <span class="stat-label">OB Hours</span>
        <span class="stat-value">{{ totalOBHours }}h</span>
      </div>
      <div class="stat-card">
        <span class="stat-label">Working Days</span>
        <span class="stat-value">{{ workingDays }}</span>
      </div>
      <div class="stat-card">
        <span class="stat-label">Pending Approval</span>
        <span class="stat-value">{{ pendingShifts }}</span>
      </div>
    </div>

    <!-- Bulk Actions -->
    <div class="bulk-actions" v-if="selectedShifts.length > 0">
      <span class="selected-count">{{ selectedShifts.length }} shift(s) selected</span>
      <button @click="bulkApprove" class="bulk-btn approve" :disabled="loading">
        ✅ Approve Selected
      </button>
      <button @click="bulkReject" class="bulk-btn reject" :disabled="loading">
        ❌ Reject Selected
      </button>
      <button @click="clearSelection" class="bulk-btn clear">
        Clear Selection
      </button>
    </div>

    <!-- Shifts List -->
    <div class="shifts-section">
      <div v-if="loading" class="loading">
        Loading shifts...
      </div>

      <div v-else-if="shifts.length === 0" class="no-shifts">
        No shifts found for {{ formatMonthYear(currentDate) }}
      </div>

      <div v-else class="shifts-list">
        <div 
          v-for="shift in shifts" 
          :key="shift.id" 
          class="shift-card"
          :class="{ 
            'selected': selectedShifts.includes(shift.id),
            'status-draft': shift.status === 'draft',
            'status-submitted': shift.status === 'submitted',
            'status-approved': shift.status === 'approved',
            'status-rejected': shift.status === 'rejected'
          }"
        >
          <div class="shift-header">
            <label class="checkbox-container">
              <input 
                type="checkbox" 
                :value="shift.id" 
                v-model="selectedShifts"
                :disabled="shift.status === 'approved'"
              >
              <span class="checkmark"></span>
            </label>
            
            <div class="shift-date">
              <span class="day">{{ formatDate(shift.date) }}</span>
              <span class="weekday">{{ formatWeekday(shift.date) }}</span>
            </div>
            
            <div class="shift-times">
              <span class="time-range">{{ shift.start_time }} - {{ shift.end_time }}</span>
              <span class="break-duration" v-if="shift.break_duration > 0">
                Break: {{ shift.break_duration }}min
              </span>
            </div>
            
            <div class="shift-hours">
              <span class="total-hours">{{ calculateShiftHours(shift) }}h</span>
              <div class="ob-hours" v-if="hasOBHours(shift)">
                <span v-if="getOB1Hours(shift) > 0" class="ob1">OB1: {{ getOB1Hours(shift) }}h</span>
                <span v-if="getOB2Hours(shift) > 0" class="ob2">OB2: {{ getOB2Hours(shift) }}h</span>
                <span v-if="getOB3Hours(shift) > 0" class="ob3">OB3: {{ getOB3Hours(shift) }}h</span>
              </div>
            </div>
            
            <div class="shift-status">
              <span class="status-badge" :class="shift.status">{{ shift.status }}</span>
              <span class="updated-time">{{ formatDateTime(shift.updated_at) }}</span>
            </div>
          </div>

          <div class="shift-details" v-if="shift.description">
            <p class="description">{{ shift.description }}</p>
          </div>

          <div class="shift-actions">
            <button 
              @click="approveShift(shift)" 
              class="action-btn approve"
              :disabled="loading || shift.status === 'approved'"
              v-if="shift.status !== 'approved'"
            >
              ✅ Approve
            </button>
            <button 
              @click="rejectShift(shift)" 
              class="action-btn reject"
              :disabled="loading || shift.status === 'rejected'"
              v-if="shift.status !== 'rejected'"
            >
              ❌ Reject
            </button>
            <button 
              @click="editShift(shift)" 
              class="action-btn edit"
              :disabled="loading"
              v-if="shift.status === 'draft'"
            >
              ✏️ Edit
            </button>
            <span v-if="shift.status === 'approved'" class="approved-info">
              Approved {{ formatDateTime(shift.updated_at) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Export Section -->
    <div class="export-section" v-if="shifts.length > 0">
      <button @click="exportToPDF" class="export-btn" :disabled="loading">
        📄 Export Monthly Report
      </button>
    </div>

    <!-- Messages -->
    <div v-if="message" :class="['message', messageType]">
      {{ message }}
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth.js'
import { timeEntryApi } from '../services/timeEntryApi.js'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const loading = ref(false)
const message = ref('')
const messageType = ref('success')
const worker = ref(null)
const shifts = ref([])
const selectedShifts = ref([])
const currentDate = ref(new Date())

const workerId = route.params.workerId

onMounted(() => {
  loadWorkerData()
  loadShifts()
})

watch(currentDate, () => {
  loadShifts()
})

// Computed properties
const totalHours = computed(() => {
  return shifts.value.reduce((total, shift) => {
    return total + parseFloat(calculateShiftHours(shift))
  }, 0).toFixed(2)
})

const totalOBHours = computed(() => {
  return shifts.value.reduce((total, shift) => {
    const ob1 = parseFloat(getOB1Hours(shift))
    const ob2 = parseFloat(getOB2Hours(shift))
    const ob3 = parseFloat(getOB3Hours(shift))
    return total + ob1 + ob2 + ob3
  }, 0).toFixed(2)
})

const workingDays = computed(() => {
  const uniqueDates = new Set(shifts.value.map(shift => shift.date))
  return uniqueDates.size
})

const pendingShifts = computed(() => {
  return shifts.value.filter(shift => 
    shift.status === 'draft' || shift.status === 'submitted'
  ).length
})

// Methods
async function loadWorkerData() {
  try {
    const response = await authStore.makeAuthenticatedRequest(`/api/users/${workerId}/`)
    
    if (response.ok) {
      worker.value = await response.json()
    } else {
      showMessage('Failed to load worker data', 'error')
    }
  } catch (error) {
    console.error('Error loading worker:', error)
    showMessage(error.message || 'Network error while loading worker data', 'error')
  }
}

async function loadShifts() {
  try {
    loading.value = true
    const year = currentDate.value.getFullYear()
    const month = currentDate.value.getMonth() + 1

    const response = await authStore.makeAuthenticatedRequest(`/api/manager/${workerId}/worker-shifts/?year=${year}&month=${month}`)

    if (response.ok) {
      shifts.value = await response.json()
      selectedShifts.value = [] // Clear selection when loading new data
    } else {
      showMessage('Failed to load shifts', 'error')
    }
  } catch (error) {
    console.error('Error loading shifts:', error)
    showMessage(error.message || 'Network error while loading shifts', 'error')
  } finally {
    loading.value = false
  }
}

async function approveShift(shift) {
  try {
    loading.value = true
    await timeEntryApi.updateShiftStatus(shift.id, 'approved', workerId)
    showMessage('Shift approved successfully', 'success')
    await loadShifts()
  } catch (error) {
    console.error('Error approving shift:', error)
    showMessage('Failed to approve shift', 'error')
  } finally {
    loading.value = false
  }
}

async function rejectShift(shift) {
  try {
    loading.value = true
    await timeEntryApi.updateShiftStatus(shift.id, 'rejected', workerId)
    showMessage('Shift rejected', 'success')
    await loadShifts()
  } catch (error) {
    console.error('Error rejecting shift:', error)
    showMessage('Failed to reject shift', 'error')
  } finally {
    loading.value = false
  }
}

async function bulkApprove() {
  try {
    loading.value = true
    
    for (const shiftId of selectedShifts.value) {
      const shift = shifts.value.find(s => s.id === shiftId)
      if (shift && shift.status !== 'approved') {
        await timeEntryApi.updateShiftStatus(shiftId, 'approved', workerId)
      }
    }
    
    showMessage(`Approved ${selectedShifts.value.length} shifts`, 'success')
    selectedShifts.value = []
    await loadShifts()
  } catch (error) {
    console.error('Error bulk approving:', error)
    showMessage('Failed to approve shifts', 'error')
  } finally {
    loading.value = false
  }
}

async function bulkReject() {
  try {
    loading.value = true
    
    for (const shiftId of selectedShifts.value) {
      const shift = shifts.value.find(s => s.id === shiftId)
      if (shift && shift.status !== 'rejected') {
        await timeEntryApi.updateShiftStatus(shiftId, 'rejected', workerId)
      }
    }
    
    showMessage(`Rejected ${selectedShifts.value.length} shifts`, 'success')
    selectedShifts.value = []
    await loadShifts()
  } catch (error) {
    console.error('Error bulk rejecting:', error)
    showMessage('Failed to reject shifts', 'error')
  } finally {
    loading.value = false
  }
}

function clearSelection() {
  selectedShifts.value = []
}

function editShift(shift) {
  // TODO: Implement edit functionality
  showMessage('Edit functionality coming soon', 'info')
}

function exportToPDF() {
  // TODO: Implement PDF export for individual worker
  showMessage('PDF export coming soon', 'info')
}

function goBack() {
  router.push('/manager')
}

function previousMonth() {
  const newDate = new Date(currentDate.value)
  newDate.setMonth(newDate.getMonth() - 1)
  currentDate.value = newDate
}

function nextMonth() {
  const newDate = new Date(currentDate.value)
  newDate.setMonth(newDate.getMonth() + 1)
  currentDate.value = newDate
}

// Utility functions
function formatMonthYear(date) {
  const monthYear = date.toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' })
  return monthYear.charAt(0).toUpperCase() + monthYear.slice(1).toLowerCase()
}

function formatDate(dateStr) {
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })
}

function formatWeekday(dateStr) {
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString('sv-SE', { weekday: 'short' })
}

function formatDateTime(dateTimeStr) {
  if (!dateTimeStr) return ''
  const date = new Date(dateTimeStr)
  return date.toLocaleDateString('sv-SE', { 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function calculateShiftHours(shift) {
  if (!shift.start_time || !shift.end_time) return '0.00'
  
  const start = new Date(`2000-01-01 ${shift.start_time}`)
  const end = new Date(`2000-01-01 ${shift.end_time}`)
  const breakMinutes = parseInt(shift.break_duration) || 0
  
  const diffMs = end - start - (breakMinutes * 60 * 1000)
  const hours = diffMs / (1000 * 60 * 60)
  
  return Math.max(0, hours).toFixed(2)
}

function hasOBHours(shift) {
  return getOB1Hours(shift) > 0 || getOB2Hours(shift) > 0 || getOB3Hours(shift) > 0
}

function getOB1Hours(shift) {
  if (!shift.start_time || !shift.end_time) return 0
  
  // Check if OB3 applies - if so, no OB1
  const date = new Date(shift.date)
  const dayOfWeek = date.getDay() // 0 = Sunday, 6 = Saturday
  
  if (dayOfWeek === 6 || dayOfWeek === 0) return 0 // Weekend, no OB1
  
  const start = parseTimeToMinutes(shift.start_time)
  const end = parseTimeToMinutes(shift.end_time)
  const breakMinutes = parseInt(shift.break_duration) || 0
  
  // OB1: 18:15 (1095 min) to 20:00 (1200 min)
  const ob1Start = 18 * 60 + 15 // 18:15
  const ob1End = 20 * 60 // 20:00
  
  let ob1Minutes = 0
  
  if (end <= start) {
    // Handle overnight shift
    if (start < ob1End) {
      const dayEnd = Math.min(end + 24 * 60, ob1End)
      ob1Minutes += Math.max(0, dayEnd - Math.max(start, ob1Start))
    }
  } else {
    // Same day shift
    const overlapStart = Math.max(start, ob1Start)
    const overlapEnd = Math.min(end, ob1End)
    ob1Minutes = Math.max(0, overlapEnd - overlapStart)
  }
  
  // Subtract break proportionally
  const totalWorkMinutes = end <= start ? (end + 24 * 60 - start) : (end - start)
  if (totalWorkMinutes > 0) {
    ob1Minutes = Math.max(0, ob1Minutes - (breakMinutes * ob1Minutes / totalWorkMinutes))
  }
  
  return Math.round((ob1Minutes / 60) * 4) / 4 // Round to quarter hours
}

function getOB2Hours(shift) {
  if (!shift.start_time || !shift.end_time) return 0
  
  const date = new Date(shift.date)
  const dayOfWeek = date.getDay()
  
  // Check if OB3 applies - if so, no OB2
  if (dayOfWeek === 6 || dayOfWeek === 0) return 0 // Weekend covered by OB3
  
  const start = parseTimeToMinutes(shift.start_time)
  const end = parseTimeToMinutes(shift.end_time)
  const breakMinutes = parseInt(shift.break_duration) || 0
  
  // OB2: 20:00 (1200 min) to 23:59 (1439 min)
  const ob2Start = 20 * 60 // 20:00
  const ob2End = 23 * 60 + 59 // 23:59
  
  let ob2Minutes = 0
  
  if (end <= start) {
    // Handle overnight shift
    if (start <= ob2End) {
      ob2Minutes += Math.max(0, ob2End - Math.max(start, ob2Start))
    }
  } else {
    // Same day shift
    const overlapStart = Math.max(start, ob2Start)
    const overlapEnd = Math.min(end, ob2End)
    ob2Minutes = Math.max(0, overlapEnd - overlapStart)
  }
  
  // Subtract break proportionally
  const totalWorkMinutes = end <= start ? (end + 24 * 60 - start) : (end - start)
  if (totalWorkMinutes > 0) {
    ob2Minutes = Math.max(0, ob2Minutes - (breakMinutes * ob2Minutes / totalWorkMinutes))
  }
  
  return Math.round((ob2Minutes / 60) * 4) / 4 // Round to quarter hours
}

function getOB3Hours(shift) {
  if (!shift.start_time || !shift.end_time) return 0
  
  const date = new Date(shift.date)
  const dayOfWeek = date.getDay()
  
  let ob3StartTime = 0
  let ob3EndTime = 24 * 60 - 1 // 23:59
  let isOB3 = false
  
  if (dayOfWeek === 6) {
    // Saturday - OB3 from 12:00
    isOB3 = true
    ob3StartTime = 12 * 60 // 12:00
  } else if (dayOfWeek === 0) {
    // Sunday - All day OB3
    isOB3 = true
    ob3StartTime = 0
  }
  
  if (!isOB3) return 0
  
  const start = parseTimeToMinutes(shift.start_time)
  const end = parseTimeToMinutes(shift.end_time)
  const breakMinutes = parseInt(shift.break_duration) || 0
  
  let ob3Minutes = 0
  
  if (end <= start) {
    // Handle overnight shift
    if (start >= ob3StartTime) {
      ob3Minutes += Math.max(0, ob3EndTime - start)
    }
    if (end <= ob3EndTime) {
      ob3Minutes += Math.max(0, end - Math.max(0, ob3StartTime))
    }
  } else {
    // Same day shift
    const overlapStart = Math.max(start, ob3StartTime)
    const overlapEnd = Math.min(end, ob3EndTime)
    ob3Minutes = Math.max(0, overlapEnd - overlapStart)
  }
  
  // Subtract break proportionally
  const totalWorkMinutes = end <= start ? (end + 24 * 60 - start) : (end - start)
  if (totalWorkMinutes > 0) {
    ob3Minutes = Math.max(0, ob3Minutes - (breakMinutes * ob3Minutes / totalWorkMinutes))
  }
  
  return Math.round((ob3Minutes / 60) * 4) / 4 // Round to quarter hours
}

function parseTimeToMinutes(timeString) {
  const [hours, minutes] = timeString.split(':').map(Number)
  return hours * 60 + minutes
}

function showMessage(text, type = 'success') {
  message.value = text
  messageType.value = type
  setTimeout(() => {
    message.value = ''
  }, 3000)
}
</script>

<style scoped>
.worker-shifts-page {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e0e0e0;
}

.back-btn {
  padding: 0.5rem 1rem;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  text-decoration: none;
}

.back-btn:hover {
  background: #5a6268;
}

.worker-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.worker-info h2 {
  margin: 0;
  color: #333;
}

.role-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: bold;
  text-transform: capitalize;
}

.role-badge.worker {
  background: #e3f2fd;
  color: #1976d2;
}

.month-navigation {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  margin-bottom: 2rem;
}

.nav-btn {
  padding: 0.5rem 1rem;
  background: #2196f3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.nav-btn:hover {
  background: #1976d2;
}

.current-month {
  margin: 0;
  color: #333;
  min-width: 200px;
  text-align: center;
}

.stats-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
  border: 1px solid #e0e0e0;
}

.stat-label {
  display: block;
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0.5rem;
}

.stat-value {
  display: block;
  font-size: 1.5rem;
  font-weight: bold;
  color: #333;
}

.bulk-actions {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.selected-count {
  font-weight: bold;
  color: #333;
}

.bulk-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
}

.bulk-btn.approve {
  background: #28a745;
  color: white;
}

.bulk-btn.reject {
  background: #dc3545;
  color: white;
}

.bulk-btn.clear {
  background: #6c757d;
  color: white;
}

.shifts-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.shift-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1.5rem;
  transition: all 0.2s ease;
}

.shift-card.selected {
  border-color: #2196f3;
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
}

.shift-card.status-approved {
  background: #f8fff8;
  border-color: #28a745;
}

.shift-card.status-rejected {
  background: #fff8f8;
  border-color: #dc3545;
}

.shift-card.status-submitted {
  background: #f8f9ff;
  border-color: #2196f3;
}

.shift-header {
  display: grid;
  grid-template-columns: auto 1fr 2fr 1fr 1fr;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1rem;
}

.checkbox-container {
  position: relative;
  cursor: pointer;
}

.checkbox-container input {
  opacity: 0;
  position: absolute;
}

.checkmark {
  width: 20px;
  height: 20px;
  background: white;
  border: 2px solid #ddd;
  border-radius: 4px;
  display: block;
}

.checkbox-container input:checked + .checkmark {
  background: #2196f3;
  border-color: #2196f3;
}

.checkbox-container input:checked + .checkmark::after {
  content: '✓';
  color: white;
  font-size: 14px;
  font-weight: bold;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.shift-date {
  display: flex;
  flex-direction: column;
}

.day {
  font-weight: bold;
  font-size: 1.1rem;
  color: #333;
}

.weekday {
  font-size: 0.9rem;
  color: #666;
}

.shift-times {
  display: flex;
  flex-direction: column;
}

.time-range {
  font-weight: bold;
  color: #333;
}

.break-duration {
  font-size: 0.9rem;
  color: #666;
}

.shift-hours {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.total-hours {
  font-weight: bold;
  font-size: 1.1rem;
  color: #333;
}

.ob-hours {
  display: flex;
  gap: 0.5rem;
  font-size: 0.8rem;
}

.ob1 { color: #ff9800; }
.ob2 { color: #f44336; }
.ob3 { color: #9c27b0; }

.shift-status {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: bold;
  text-transform: uppercase;
}

.status-badge.draft {
  background: #f5f5f5;
  color: #666;
}

.status-badge.submitted {
  background: #e3f2fd;
  color: #1976d2;
}

.status-badge.approved {
  background: #e8f5e8;
  color: #4caf50;
}

.status-badge.rejected {
  background: #ffebee;
  color: #f44336;
}

.updated-time {
  font-size: 0.8rem;
  color: #666;
  margin-top: 0.25rem;
}

.shift-details {
  margin: 1rem 0;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
}

.description {
  margin: 0;
  color: #333;
  font-style: italic;
}

.shift-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.action-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.action-btn.approve {
  background: #28a745;
  color: white;
}

.action-btn.approve:hover:not(:disabled) {
  background: #218838;
}

.action-btn.reject {
  background: #dc3545;
  color: white;
}

.action-btn.reject:hover:not(:disabled) {
  background: #c82333;
}

.action-btn.edit {
  background: #ffc107;
  color: #333;
}

.action-btn.edit:hover:not(:disabled) {
  background: #e0a800;
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.approved-info {
  color: #28a745;
  font-size: 0.9rem;
  font-style: italic;
}

.export-section {
  margin-top: 2rem;
  text-align: center;
}

.export-btn {
  padding: 1rem 2rem;
  background: #2196f3;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
}

.export-btn:hover:not(:disabled) {
  background: #1976d2;
}

.loading, .no-shifts {
  text-align: center;
  padding: 3rem;
  color: #666;
}

.message {
  position: fixed;
  top: 2rem;
  right: 2rem;
  padding: 1rem 1.5rem;
  border-radius: 6px;
  color: white;
  font-weight: bold;
  z-index: 1000;
  animation: slideIn 0.3s ease;
}

.message.success {
  background: #4caf50;
}

.message.error {
  background: #f44336;
}

.message.info {
  background: #2196f3;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .worker-shifts-page {
    padding: 1rem;
  }
  
  .header {
    flex-direction: column;
    gap: 1rem;
  }
  
  .shift-header {
    grid-template-columns: auto 1fr;
    gap: 0.5rem;
  }
  
  .shift-times,
  .shift-hours,
  .shift-status {
    grid-column: 1 / -1;
    margin-top: 0.5rem;
  }
  
  .stats-summary {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .bulk-actions {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>